import { useMemo, useState } from "react";

import Button from "components/Button";
import HollowButton from "components/HollowButton";

import parcelService from "api/parcelService";
import mailroomService from "api/mailroomService";
import { IDispatchListItem } from "dto/Mailroom/IDispatchChart";
import { ENotificationType, notification } from "utils/notification";

import styles from "./outgoing.module.scss";

interface Props {
  item: IDispatchListItem;
  onClose: () => void;
  onDone: () => void;
}

// Zadanie 2: „Zmień sposób nadania" — zmiana formy doręczenia (ParcelDeliveryKind)
// pojedynczej przesyłki wychodzącej na liście „Do wysyłki" (np. List ZPO → List polecony).
const ChangeDeliveryKindModal = ({ item, onClose, onDone }: Props) => {
  const { mutate, isLoading } = parcelService.useChangeDeliveryKind();
  const { data: kindsData, isLoading: loadingKinds } =
    mailroomService.useGetDeliveryKinds();

  // Model szyfrowania obecnej formy (elektroniczna vs fizyczna). Zmiana sposobu nadania
  // może zmienić kategorię i kanał (parcel.Type), ale TYLKO w obrębie tego samego modelu —
  // BE blokuje przejścia fizyczny↔elektroniczny (rozjazd szyfrowania załączników).
  const currentIsElectronic = useMemo(() => {
    const cur = (kindsData?.data ?? []).find((k) => k.Id === item.DeliveryKindId);
    return cur?.IsElectronic ?? false;
  }, [kindsData, item.DeliveryKindId]);

  // Pokazujemy realne formy tego samego modelu szyfrowania, z pominięciem „Inne"
  // (forma-zaślepka, nie realny sposób nadania).
  const kinds = useMemo(() => {
    return (kindsData?.data ?? [])
      .filter(
        (k) => k.Name !== "Inne" && (k.IsElectronic ?? false) === currentIsElectronic,
      )
      .slice()
      .sort((a, b) => a.SortOrder - b.SortOrder);
  }, [kindsData, currentIsElectronic]);

  const [kindId, setKindId] = useState<number | null>(
    item.DeliveryKindId ?? null,
  );

  const changed = kindId != null && kindId !== item.DeliveryKindId;

  const submit = () => {
    if (kindId == null) return;
    mutate(
      { ParcelId: item.Id, DeliveryKindId: kindId },
      {
        onSuccess: () => {
          notification({
            type: ENotificationType.SUCCESS,
            title: "Zmieniono sposób nadania",
            text: `Przesyłka ${item.ParcelNumber ?? ""} — nowa forma doręczenia.`,
          });
          onDone();
        },
        onError: (e) =>
          notification({
            type: ENotificationType.ERROR,
            title: "Błąd",
            text:
              (e as { response?: { data?: { Message?: string } } })?.response
                ?.data?.Message ??
              "Nie udało się zmienić sposobu nadania.",
          }),
      },
    );
  };

  return (
    <div className={styles.overlay} onMouseDown={onClose}>
      <div className={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
        <div className={styles.modalTitle}>Zmień sposób nadania</div>
        <div className={styles.modalBody}>
          <div className={styles.modalRow}>
            <span>Adresat:</span>
            <strong>{item.ReceiverName ?? "—"}</strong>
          </div>
          {item.ParcelNumber && (
            <div className={styles.modalRow}>
              <span>Sygnatura:</span>
              <strong>{item.ParcelNumber}</strong>
            </div>
          )}
          <div className={styles.modalRow}>
            <span>Obecna forma:</span>
            <strong>{item.DeliveryKindName ?? "—"}</strong>
          </div>

          <div className={styles.field}>
            <label>Nowa forma doręczenia</label>
            <select
              value={kindId ?? ""}
              onChange={(e) =>
                setKindId(e.target.value === "" ? null : Number(e.target.value))
              }
              disabled={loadingKinds}
            >
              {loadingKinds && <option value="">Ładowanie…</option>}
              {kinds.map((k) => (
                <option key={k.Id} value={k.Id}>
                  {k.Name}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.hint}>
            Zmiana możliwa dla przesyłek jeszcze nienadanych i niedołączonych do
            karty nadawczej.
          </div>
        </div>
        <div className={styles.modalFooter}>
          <HollowButton text="Anuluj" onClick={onClose} />
          <Button
            text="Zapisz"
            onClick={submit}
            loading={isLoading}
            disabled={!changed}
          />
        </div>
      </div>
    </div>
  );
};

export default ChangeDeliveryKindModal;
